/**
 * Progress Component
 * Fonctions utilitaires pour gérer les barres de progression
 */

/**
 * Met à jour la valeur d'une barre de progression
 * @param {HTMLElement|string} progressElement - L'élément progress ou son sélecteur
 * @param {number} value - La valeur (0-100)
 */
// aria-valuenow sur l'élément role="progressbar" (lu par les lecteurs d'écran)
function setValueNow(element, value) {
  const host = element.matches('[role="progressbar"]') ? element : element.querySelector('[role="progressbar"]');
  host?.setAttribute('aria-valuenow', String(Math.round(value)));
}

export function setProgressValue(progressElement, value) {
  const element = typeof progressElement === 'string'
    ? document.querySelector(progressElement)
    : progressElement;

  if (!element) return;

  const bar = element.querySelector('.progress__bar');
  // Le pourcentage est souvent à côté de la barre (progress__label), pas dedans
  const percentage = element.querySelector('.progress__percentage')
    || element.parentElement?.querySelector('.progress__percentage');

  // Clamp la valeur entre 0 et 100
  const clampedValue = Math.max(0, Math.min(100, value));

  if (bar) bar.style.width = `${clampedValue}%`;
  setValueNow(element, clampedValue);

  if (percentage) {
    percentage.textContent = `${Math.round(clampedValue)}%`;
  }
}

/**
 * Met à jour une progress bar circulaire
 * @param {HTMLElement|string} circleElement - L'élément circle ou son sélecteur
 * @param {number} value - La valeur (0-100)
 */
export function setCircleProgressValue(circleElement, value) {
  const element = typeof circleElement === 'string'
    ? document.querySelector(circleElement)
    : circleElement;

  if (!element) return;

  const bar = element.querySelector('.progress-circle__bar');
  const label = element.querySelector('.progress-circle__label');

  // Clamp la valeur entre 0 et 100
  const clampedValue = Math.max(0, Math.min(100, value));

  if (bar) {
    const radius = parseFloat(bar.getAttribute('r'));
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (clampedValue / 100) * circumference;

    bar.style.strokeDasharray = circumference;
    bar.style.strokeDashoffset = offset;
  }

  if (label) {
    label.textContent = `${Math.round(clampedValue)}%`;
  }
  setValueNow(element, clampedValue);
}

/**
 * Anime une barre de progression de 0 à une valeur donnée
 * @param {HTMLElement|string} progressElement - L'élément progress ou son sélecteur
 * @param {number} targetValue - La valeur cible (0-100)
 * @param {number} duration - Durée de l'animation en ms (défaut: 1000)
 */
export function animateProgress(progressElement, targetValue, duration = 1000) {
  const element = typeof progressElement === 'string'
    ? document.querySelector(progressElement)
    : progressElement;

  if (!element) return;

  const startTime = Date.now();
  const startValue = 0;
  const endValue = Math.max(0, Math.min(100, targetValue));

  function animate() {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function (ease-out)
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    const currentValue = startValue + (endValue - startValue) * easeProgress;

    setProgressValue(element, currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  }

  requestAnimationFrame(animate);
}

/**
 * Anime une progress bar circulaire de 0 à une valeur donnée
 * @param {HTMLElement|string} circleElement - L'élément circle ou son sélecteur
 * @param {number} targetValue - La valeur cible (0-100)
 * @param {number} duration - Durée de l'animation en ms (défaut: 1000)
 */
export function animateCircleProgress(circleElement, targetValue, duration = 1000) {
  const element = typeof circleElement === 'string'
    ? document.querySelector(circleElement)
    : circleElement;

  if (!element) return;

  const startTime = Date.now();
  const startValue = 0;
  const endValue = Math.max(0, Math.min(100, targetValue));

  function animate() {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function (ease-out)
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    const currentValue = startValue + (endValue - startValue) * easeProgress;

    setCircleProgressValue(element, currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  }

  requestAnimationFrame(animate);
}

/**
 * Initialise les barres de progression
 * Pas d'initialisation nécessaire car le composant est purement CSS
 * Cette fonction est conservée pour la compatibilité avec l'architecture
 */
export function initProgress() {
  // API globale : utilisable sans import de module (bundle IIFE, npm)
  if (typeof window !== 'undefined') {
    window.Progress = { setProgressValue, setCircleProgressValue, animateProgress, animateCircleProgress };
  }
}
